import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Layers, Lightbulb, LayoutTemplate, History, Plus } from 'lucide-react';
import { automationService } from '../../services/automationService';
import { AutomationRule, AutomationStats, ExecutionLog, AISuggestion } from '../../types';
import AutomationStatsCards from '../automation/AutomationStatsCards';
import AutomationRulesList from '../automation/AutomationRulesList';
import AutomationRuleForm from '../automation/AutomationRuleForm';
import AutomationTemplates from '../automation/AutomationTemplates';
import AutomationExecutionLogs from '../automation/AutomationExecutionLogs';
import AutomationAISuggestions from '../automation/AutomationAISuggestions';
import { AutomationTemplateData } from '../automation/automationHelpers';

type SubTab = 'rules' | 'suggestions' | 'templates' | 'history';

interface CampaignAutomationHubProps {
  campaignId: string;
}

const CampaignAutomationHub: React.FC<CampaignAutomationHubProps> = ({ campaignId }) => {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('rules');
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [stats, setStats] = useState<AutomationStats>({
    activeRules: 0, totalExecutions: 0, successRate: 0,
    savingsEstimate: 0, performanceImprovement: 0, lastDayExecutions: 0
  });
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectedPlatforms, setConnectedPlatforms] = useState<string[]>([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [formData, setFormData] = useState<any>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');

  useEffect(() => {
    loadData();
  }, [campaignId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [rulesRes, statsRes, logsRes, suggestionsRes, platforms] = await Promise.all([
        automationService.getRules(campaignId),
        automationService.getStats(campaignId),
        automationService.getLogs(campaignId),
        automationService.getSuggestions(campaignId),
        automationService.getConnectedPlatforms()
      ]);
      if (rulesRes.success && rulesRes.data) setRules(rulesRes.data);
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
      if (logsRes.success && logsRes.data) setLogs(logsRes.data);
      if (suggestionsRes.success && suggestionsRes.data) setAiSuggestions(suggestionsRes.data);
      setConnectedPlatforms(platforms);
    } catch (error) {
      console.error('Error loading automation data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingRule(null);
    setFormData({
      name: '', description: '',
      platform: connectedPlatforms.length > 0 ? connectedPlatforms : ['google', 'meta', 'linkedin'],
      trigger_type: 'ctr_below', trigger_value: 0,
      action_type: 'adjust_bid', action_value: 0,
      frequency: 'daily', status: 'active',
      max_executions: 50, max_cumulative_change: 100,
      campaign_ids: [campaignId]
    });
    setShowForm(true);
  };

  const handleEdit = (rule: AutomationRule) => {
    setEditingRule(rule);
    const platformArray = Array.isArray(rule.platform) ? rule.platform
      : (rule.platform as any) === 'all' || (rule.platform as any) === 'both'
        ? ['google', 'meta', 'linkedin'] : [rule.platform as any];
    setFormData({
      name: rule.name, description: rule.description, platform: platformArray,
      trigger_type: rule.trigger_type, trigger_value: rule.trigger_value,
      action_type: rule.action_type, action_value: rule.action_value,
      frequency: rule.frequency, status: rule.status,
      max_executions: rule.max_executions || null,
      max_cumulative_change: rule.max_cumulative_change || null
    });
    setShowForm(true);
  };

  const handleSubmit = async (data: any) => {
    try {
      if (editingRule) {
        await automationService.updateRule(editingRule.id, data);
        toast.success(t('automation.toasts.ruleUpdated', 'Rule updated'));
      } else {
        await automationService.createRule({ ...data, campaign_ids: [campaignId] });
        toast.success(t('automation.toasts.ruleCreated', 'Rule created'));
      }
      setShowForm(false);
      setEditingRule(null);
      loadData();
    } catch (error) {
      toast.error(t('automation.toasts.errorSavingRule', 'Error saving rule'));
    }
  };

  const handleToggleStatus = async (rule: AutomationRule) => {
    try {
      const newStatus = rule.status === 'active' ? 'paused' : 'active';
      await automationService.toggleStatus(rule.id, newStatus);
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, status: newStatus } : r));
      toast.success(newStatus === 'active' ? t('automation.rule.activated', 'Activated') : t('automation.rule.paused', 'Paused'));
    } catch (error) {
      toast.error(t('automation.rule.errorChangingStatus', 'Error changing status'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('automation.confirmDelete', 'Delete this rule?'))) return;
    try {
      await automationService.deleteRule(id);
      toast.success(t('automation.toasts.ruleDeleted', 'Rule deleted'));
      loadData();
    } catch (error) {
      toast.error(t('automation.toasts.errorDeletingRule', 'Error deleting rule'));
    }
  };

  const handleUseTemplate = (template: AutomationTemplateData) => {
    setEditingRule(null);
    setFormData({
      name: template.name, description: template.description,
      platform: template.platform,
      trigger_type: template.trigger_type, trigger_value: template.trigger_value,
      action_type: template.action_type, action_value: template.action_value,
      frequency: template.frequency, status: 'active',
      max_executions: 50, max_cumulative_change: 100,
      campaign_ids: [campaignId]
    });
    setShowForm(true);
  };

  const handleCreateFromSuggestion = (suggestion: AISuggestion) => {
    const platformArray = suggestion.platform === 'google' ? ['google']
      : suggestion.platform === 'meta' ? ['meta']
      : suggestion.platform === 'linkedin' ? ['linkedin']
      : ['google', 'meta', 'linkedin'];
    setEditingRule(null);
    setFormData({
      name: suggestion.title, description: suggestion.description,
      platform: platformArray,
      trigger_type: suggestion.trigger_type || 'ctr_below',
      trigger_value: suggestion.trigger_value ?? 0,
      action_type: suggestion.action_type || 'adjust_bid',
      action_value: suggestion.action_value ?? 0,
      frequency: suggestion.frequency || 'daily',
      status: 'active',
      max_executions: 50, max_cumulative_change: 100,
      campaign_ids: [campaignId]
    });
    setShowForm(true);
  };

  const subTabs = [
    { key: 'rules' as const, label: t('campaigns.automationHub.myRules', 'My Rules'), icon: <Layers className="w-4 h-4" /> },
    { key: 'suggestions' as const, label: t('campaigns.automationHub.aiSuggestions', 'AI Suggestions'), icon: <Lightbulb className="w-4 h-4" /> },
    { key: 'templates' as const, label: t('campaigns.automationHub.templates', 'Templates'), icon: <LayoutTemplate className="w-4 h-4" /> },
    { key: 'history' as const, label: t('campaigns.automationHub.history', 'History'), icon: <History className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-4">
      {/* Stats */}
      <AutomationStatsCards stats={stats} />

      {/* Sub-tabs + New Rule button */}
      <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
        <div className="grid grid-cols-4 sm:flex items-center gap-1 sm:gap-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full sm:w-fit sm:flex-wrap">
          {subTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveSubTab(tab.key)}
              className={`flex items-center justify-center gap-2 px-1 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors min-w-0 text-center leading-tight ${
                activeSubTab === tab.key
                  ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <span className="hidden sm:inline-flex">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        {activeSubTab === 'rules' && (
          <button onClick={handleCreateNew} className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto whitespace-nowrap">
            <Plus className="w-4 h-4" />
            {t('automation.newRule', 'New Rule')}
          </button>
        )}
      </div>

      {/* Tab content */}
      {activeSubTab === 'rules' && (
        <AutomationRulesList
          rules={rules}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filterStatus={filterStatus}
          onFilterStatusChange={setFilterStatus}
          filterPlatform={filterPlatform}
          onFilterPlatformChange={setFilterPlatform}
          onToggleStatus={handleToggleStatus}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCreateNew={handleCreateNew}
          onOpenTemplates={() => setActiveSubTab('templates')}
        />
      )}

      {activeSubTab === 'suggestions' && (
        <AutomationAISuggestions
          suggestions={aiSuggestions}
          onCreateFromSuggestion={handleCreateFromSuggestion}
        />
      )}

      {activeSubTab === 'templates' && (
        <AutomationTemplates onUseTemplate={handleUseTemplate} />
      )}

      {activeSubTab === 'history' && (
        <RuleExecutionHistory rules={rules} logs={logs} />
      )}

      {/* Rule form modal */}
      <AutomationRuleForm
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingRule(null); }}
        onSubmit={handleSubmit}
        editingRule={editingRule}
        connectedPlatforms={connectedPlatforms}
        initialData={formData}
      />
    </div>
  );
};

// Inline execution history component that merges rule.execution_history entries
const RuleExecutionHistory: React.FC<{ rules: AutomationRule[]; logs: ExecutionLog[] }> = ({ rules, logs }) => {
  const { t } = useTranslation();

  // Merge execution_history from all rules into a single sorted timeline
  const allEntries: Array<{
    id: string;
    ruleName: string;
    ruleId: string;
    success: boolean;
    message: string;
    timestamp: string;
    campaignName?: string;
    triggerReason?: string;
    triggeredBy?: string;
    metricValue?: string;
    threshold?: number;
  }> = [];

  for (const rule of rules) {
    const history = rule.execution_history || [];
    for (let i = 0; i < history.length; i++) {
      const entry = history[i];
      allEntries.push({
        id: `${rule.id}-${i}`,
        ruleName: rule.name,
        ruleId: rule.id,
        success: entry.success !== false,
        message: entry.message || '',
        timestamp: entry.timestamp || '',
        campaignName: entry.campaign_name,
        triggerReason: entry.trigger_reason,
        triggeredBy: entry.triggered_by || 'cron',
        metricValue: entry.metric_value,
        threshold: entry.threshold
      });
    }
  }

  // Sort by timestamp desc
  allEntries.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const formatTime = (ts: string) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `${diffMin}min atrás`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h atrás`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d atrás`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (allEntries.length === 0 && logs.length === 0) {
    return (
      <div className="text-center py-12">
        <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400">{t('automation.logs.empty', 'Nenhum histórico de execução')}</p>
        <p className="text-xs text-gray-400 mt-1">As automações ativas são verificadas a cada 30 minutos</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-400 mb-3">
        {t('automation.history.safetyInfo', 'Verificação automática a cada 30 min · Limites: Lance máx R$500, Orçamento máx R$50k, máx 2x por execução, limite acumulado configurável, pausa automática ao atingir limite de execuções')}
      </p>
      {allEntries.map((entry) => (
        <div key={entry.id} className="flex flex-wrap sm:flex-nowrap items-start gap-x-3 gap-y-1 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <div className={`p-1.5 rounded-lg mt-0.5 ${entry.success ? 'bg-green-100 dark:bg-green-900/40' : 'bg-red-100 dark:bg-red-900/40'}`}>
            {entry.success ? (
              <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            ) : (
              <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 min-w-0">
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate min-w-0">{entry.ruleName}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                entry.triggeredBy === 'manual'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {entry.triggeredBy === 'manual' ? 'manual' : 'auto'}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 break-words">{entry.message}</p>
            {entry.triggerReason && (
              <p className="text-[11px] text-gray-400 mt-0.5">Gatilho: {entry.triggerReason}</p>
            )}
            {entry.campaignName && (
              <p className="text-[11px] text-gray-400">Campanha: {entry.campaignName}</p>
            )}
          </div>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">{formatTime(entry.timestamp)}</span>
        </div>
      ))}
    </div>
  );
};

export default CampaignAutomationHub;
